import { useRef, useState } from "react";
import { KfAvatar } from "./KfAvatar";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Camera, UserPlus, UserMinus, Pencil, Image as ImageIcon } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

function knightSinceLabel(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

// Compact Knight profile strip — used both on your own Knights Fee page and on any other
// Knight's `/knights-fee/u/:userId` page. Lets you edit your own avatar/banner/bio inline, or
// follow/unfollow anyone else.
export function KfProfileCard({ profile, onProfileUpdate, onFollowChange, gauntletBadge, ranks }) {
  const [editingBio, setEditingBio] = useState(false);
  const [bioDraft, setBioDraft] = useState(profile.bio || "");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const fileInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploadingAvatar(true);
    try {
      const { data } = await api.post("/uploads?kind=kf_avatar", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/kf/profile", { avatar_url: data.url });
      onProfileUpdate({ ...profile, avatar_url: data.url });
      toast.success("Looking sharp, Knight");
    } catch {
      toast.error("Couldn't update your avatar");
    } finally {
      setUploadingAvatar(false);
    }
  };

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploadingBanner(true);
    try {
      const { data } = await api.post("/uploads?kind=kf_banner", formData, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/kf/profile", { banner_url: data.url });
      onProfileUpdate({ ...profile, banner_url: data.url });
      toast.success("Banner updated");
    } catch {
      toast.error("Couldn't update your banner");
    } finally {
      setUploadingBanner(false);
    }
  };

  const saveBio = async () => {
    try {
      await api.patch("/kf/profile", { bio: bioDraft });
      onProfileUpdate({ ...profile, bio: bioDraft.trim().slice(0, 280) });
      setEditingBio(false);
    } catch {
      toast.error("Couldn't save your bio");
    }
  };

  const toggleFollow = async () => {
    setFollowBusy(true);
    try {
      if (profile.is_following) await api.delete(`/kf/follow/${profile.id}`);
      else await api.post(`/kf/follow/${profile.id}`);
      onFollowChange({ ...profile, is_following: !profile.is_following, follower_count: profile.follower_count + (profile.is_following ? -1 : 1) });
    } catch {
      toast.error("Couldn't update that follow");
    } finally {
      setFollowBusy(false);
    }
  };

  return (
    <div data-testid="kf-profile-card" className="rounded-xl border border-white/10 bg-black/20 overflow-hidden">
      <div
        data-testid="kf-profile-banner"
        className="relative h-28 sm:h-36 bg-cover bg-center bg-[#1c140c]"
        style={profile.banner_url ? { backgroundImage: `url('${profile.banner_url}')` } : undefined}
      >
        {!profile.banner_url && <div className="absolute inset-0 bg-gradient-to-br from-[#3a2a17] to-[#1c140c]" />}
        <div className="absolute inset-0 bg-black/25" />
        {profile.is_self && (
          <>
            <input ref={bannerInputRef} type="file" accept="image/*" onChange={uploadBanner} className="hidden" data-testid="kf-banner-file-input" />
            <button
              data-testid="kf-edit-banner-button"
              onClick={() => bannerInputRef.current?.click()}
              disabled={uploadingBanner}
              className="absolute top-2 right-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded-full bg-black/50 hover:bg-black/70 text-[#f1e0c5]"
            >
              <ImageIcon className="w-3 h-3" /> {uploadingBanner ? "Uploading…" : "Banner"}
            </button>
          </>
        )}
      </div>

      <div className="p-5 space-y-4">
        <div className="flex items-start gap-4 -mt-10">
          <div className="relative">
            <KfAvatar name={profile.name} avatarUrl={profile.avatar_url} isMerchant={profile.is_merchant} size="lg" gauntletBadge={gauntletBadge} />
            {profile.is_self && (
              <>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="kf-avatar-file-input" />
                <button
                  data-testid="kf-edit-avatar-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute -bottom-1 -left-1 w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center border-2 border-[#151613]"
                >
                  <Camera className="w-3 h-3 text-black" />
                </button>
              </>
            )}
          </div>
          <div className="flex-1 min-w-0 mt-10">
            <h2 className="font-display text-xl truncate" data-testid="kf-profile-name">{profile.name}</h2>
            <p className="text-[11px] text-[#f1e0c5]/40">Knight since {knightSinceLabel(profile.knight_since)}</p>
            <div className="flex gap-4 mt-1.5 text-xs text-[#f1e0c5]/60">
              <span data-testid="kf-follower-count"><strong className="text-[#f1e0c5]">{profile.follower_count}</strong> Followers</span>
              <span data-testid="kf-following-count"><strong className="text-[#f1e0c5]">{profile.following_count}</strong> Following</span>
            </div>
            {ranks && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="kf-profile-rank-pills">
                {Object.entries(ranks).map(([ruleset, data]) => (
                  <span key={ruleset} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1E3A8A]/25 text-[#3B82F6]" data-testid={`kf-profile-rank-pill-${ruleset}`}>
                    {ruleset === "pf2e" ? "PF2e" : ruleset === "dnd5e" ? "5e" : "Homebrew"} · {data.rank_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {!profile.is_self && (
          <Button
            data-testid="kf-follow-toggle-button"
            onClick={toggleFollow}
            disabled={followBusy}
            variant={profile.is_following ? "secondary" : "default"}
            className={`w-full rounded-full uppercase tracking-wider text-xs gap-2 ${!profile.is_following ? "bg-[#D90429] hover:bg-[#b8031f]" : ""}`}
          >
            {profile.is_following ? <><UserMinus className="w-3.5 h-3.5" /> Unfollow</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
          </Button>
        )}

        {editingBio ? (
          <div className="space-y-2">
            <Textarea data-testid="kf-bio-textarea" value={bioDraft} onChange={(e) => setBioDraft(e.target.value)} maxLength={280} className="bg-black/30 border-white/10 text-sm" />
            <div className="flex gap-2">
              <Button data-testid="kf-bio-save-button" size="sm" onClick={saveBio} className="rounded-full bg-[#D4AF37] text-black hover:bg-[#c19b2e] text-xs">Save</Button>
              <Button data-testid="kf-bio-cancel-button" size="sm" variant="ghost" onClick={() => { setEditingBio(false); setBioDraft(profile.bio || ""); }} className="rounded-full text-xs">Cancel</Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <p className="text-sm text-[#f1e0c5]/70 leading-relaxed" data-testid="kf-bio-text">{profile.bio || (profile.is_self ? "Tell your fellow Knights about yourself…" : "")}</p>
            {profile.is_self && (
              <button data-testid="kf-edit-bio-button" onClick={() => setEditingBio(true)} className="text-[#f1e0c5]/30 hover:text-[#D4AF37] shrink-0">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
